import * as THREE from "three";

const DEFAULT_SIZE = 256;

const DEFAULT_TONES = [0.06, 0.14, 0.25, 0.39, 0.56, 0.78];

const DEFAULT_LEVELS = 5;

/*
 * Candidate search quality.
 *
 * These are cheap candidates: no temporary
 * canvases or image pyramids are created
 * while evaluating them.
 */
const MAX_CANDIDATES = 180;
const MIN_CANDIDATES = 60;

/* =========================================================
   Deterministic random
   ========================================================= */

function createRandom(seed = 1337) {
  let state = seed >>> 0;

  return () => {
    state += 0x6d2b79f5;

    let value = state;

    value = Math.imul(value ^ (value >>> 15), value | 1);

    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);

    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

/* =========================================================
   Canvas helpers
   ========================================================= */

function makeCanvas(size) {
  const canvas = document.createElement("canvas");

  canvas.width = size;

  canvas.height = size;

  const context = canvas.getContext("2d", {
    willReadFrequently: true,
  });

  context.fillStyle = "#ffffff";

  context.fillRect(0, 0, size, size);

  return {
    canvas,
    context,
  };
}

function canvasToRGBA(canvas) {
  const context = canvas.getContext("2d", {
    willReadFrequently: true,
  });

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

  return new Uint8Array(imageData.data);
}

function downsampleRGBA(sourceData, sourceWidth, sourceHeight) {
  const targetWidth = Math.max(1, sourceWidth >> 1);

  const targetHeight = Math.max(1, sourceHeight >> 1);

  const targetData = new Uint8Array(targetWidth * targetHeight * 4);

  for (let y = 0; y < targetHeight; y += 1) {
    for (let x = 0; x < targetWidth; x += 1) {
      const targetIndex = (y * targetWidth + x) * 4;

      for (let channel = 0; channel < 4; channel += 1) {
        let sum = 0;

        for (let oy = 0; oy < 2; oy += 1) {
          for (let ox = 0; ox < 2; ox += 1) {
            const sx = Math.min(x * 2 + ox, sourceWidth - 1);

            const sy = Math.min(y * 2 + oy, sourceHeight - 1);

            const sourceIndex = (sy * sourceWidth + sx) * 4 + channel;

            sum += sourceData[sourceIndex];
          }
        }

        targetData[targetIndex + channel] = Math.round(sum / 4);
      }
    }
  }

  return {
    data: targetData,

    width: targetWidth,

    height: targetHeight,
  };
}

/* =========================================================
   Darkness
   ========================================================= */

function measureDarkness(context, size) {
  const image = context.getImageData(0, 0, size, size);

  const data = image.data;

  let darkness = 0;

  for (let i = 0; i < data.length; i += 4) {
    darkness += 1 - data[i] / 255;
  }

  return darkness / (size * size);
}

/* =========================================================
   Toroidal geometry
   ========================================================= */

function wrappedDistance01(a, b) {
  const distance = Math.abs(a - b);

  return Math.min(distance, 1 - distance);
}

function pointDistanceToroidal(a, b) {
  const dx = wrappedDistance01(a.x, b.x);

  const dy = wrappedDistance01(a.y, b.y);

  return Math.sqrt(dx * dx + dy * dy);
}

/* =========================================================
   Stroke representation
   ========================================================= */

/*
 * All geometry is stored in normalized
 * texture coordinates.
 *
 * This makes a stroke created at 16px
 * represent exactly the same stroke when
 * propagated to 32, 64, 128 and 256.
 */
function createStroke(random, sourceSize, angle) {
  const pixelWidth = 1.0 + random() * 0.65;

  return {
    x: random(),

    y: random(),

    /*
     * Praun-style relatively long strokes.
     *
     * 0.3 -> 1.0 texture widths.
     */
    length: 0.3 + random() * 0.7,

    angle: angle + (random() - 0.5) * 0.1,

    /*
     * Store width normalized relative to
     * the level where this stroke entered
     * the hierarchy.
     */
    width: pixelWidth,

    sourceSize,
  };
}

/* =========================================================
   Stroke rendering
   ========================================================= */

function drawWrappedStroke(context, stroke, size) {
  const x = stroke.x * size;

  const y = stroke.y * size;

  const length = stroke.length * size;

  /*
   * Because width is normalized, inherited
   * strokes scale geometrically as they are
   * propagated into finer TAM levels.
   */
  const width = stroke.width;

  const dx = Math.cos(stroke.angle) * length * 0.5;

  const dy = Math.sin(stroke.angle) * length * 0.5;

  const offsets = [-size, 0, size];

  context.save();

  context.strokeStyle = "#000000";

  context.lineWidth = width;

  context.lineCap = "round";

  for (const offsetX of offsets) {
    for (const offsetY of offsets) {
      context.beginPath();

      context.moveTo(
        x - dx + offsetX,

        y - dy + offsetY,
      );

      context.lineTo(
        x + dx + offsetX,

        y + dy + offsetY,
      );

      context.stroke();
    }
  }

  context.restore();
}

/* =========================================================
   Hatch directions
   ========================================================= */

function chooseAngle(toneIndex, random) {
  const horizontal = 0.0;

  const vertical = Math.PI * 0.5;

  /*
   * Light tones:
   * first hatch family.
   *
   * Darker tones:
   * introduce perpendicular cross-hatching.
   *
   * Darkest tone:
   * additions may use either family.
   */
  if (toneIndex <= 2) {
    return horizontal;
  }

  if (toneIndex <= 4) {
    return vertical;
  }

  return random() < 0.5 ? horizontal : vertical;
}

function candidateCountForTone(toneIndex, toneCount) {
  const position = toneIndex / Math.max(toneCount - 1, 1);

  return Math.round(
    THREE.MathUtils.lerp(MAX_CANDIDATES, MIN_CANDIDATES, position),
  );
}

/* =========================================================
   Fast candidate scoring
   ========================================================= */

/*
 * Instead of rendering every candidate,
 * score its placement against existing
 * strokes.
 *
 * Large distance from existing stroke
 * centers is preferred.
 *
 * A small directional penalty discourages
 * parallel strokes from forming obvious
 * local clumps.
 */
function candidateScore(candidate, strokes) {
  if (strokes.length === 0) {
    return 1.0;
  }

  let nearest = Infinity;

  let localPenalty = 0;

  for (const stroke of strokes) {
    const distance = pointDistanceToroidal(candidate, stroke);

    nearest = Math.min(nearest, distance);

    if (distance < 0.12) {
      const alignment = Math.abs(Math.cos(candidate.angle - stroke.angle));

      localPenalty += alignment * (0.12 - distance);
    }
  }

  return nearest - localPenalty * 0.15;
}

/*
 * A stroke introduced at a coarse level
 * will also appear in every finer level.
 *
 * Score it against all of those receiving
 * levels, rather than only its source cell.
 */
function scoreAcrossLevels(candidate, column, sourceLevel) {
  let score = 0;

  let weight = 1.0;

  for (let level = sourceLevel; level < column.length; level += 1) {
    score += candidateScore(candidate, column[level].strokes) * weight;

    /*
     * Give the source level the strongest
     * influence while still considering
     * its effect on finer levels.
     */
    weight *= 0.7;
  }

  return score;
}

function chooseBestStroke(
  random,
  sourceSize,
  toneIndex,
  toneCount,
  column,
  sourceLevel,
) {
  const candidateCount = candidateCountForTone(toneIndex, toneCount);

  let bestStroke = null;

  let bestScore = -Infinity;

  for (let i = 0; i < candidateCount; i += 1) {
    const angle = chooseAngle(toneIndex, random);

    const candidate = createStroke(random, sourceSize, angle);

    const score = scoreAcrossLevels(candidate, column, sourceLevel);

    if (score > bestScore) {
      bestScore = score;

      bestStroke = candidate;
    }
  }

  return bestStroke;
}

/* =========================================================
   TAM cells
   ========================================================= */

function createCell(size) {
  const { canvas, context } = makeCanvas(size);

  return {
    size,

    canvas,

    context,

    strokes: [],

    darkness: 0,
  };
}

function createScaleDefinitions(finestSize, levels) {
  const definitions = [];

  /*
   * Internal order is deliberately:
   *
   * 16
   * 32
   * 64
   * 128
   * 256
   *
   * coarse -> fine
   */
  for (let level = levels - 1; level >= 0; level -= 1) {
    definitions.push(Math.max(16, finestSize >> level));
  }

  return definitions;
}

function createColumn(scaleDefinitions) {
  return scaleDefinitions.map((size) => createCell(size));
}

/* =========================================================
   Tone inheritance
   ========================================================= */

/*
 * T(n + 1) begins with every stroke that
 * already existed in T(n).
 *
 * We copy stroke identities and redraw
 * them rather than scaling pixels.
 */
function inheritPreviousTone(previousColumn, column) {
  if (!previousColumn) {
    return;
  }

  for (let level = 0; level < column.length; level += 1) {
    const previous = previousColumn[level];

    const current = column[level];

    current.strokes = [...previous.strokes];

    for (const stroke of current.strokes) {
      drawWrappedStroke(current.context, stroke, current.size);
    }

    current.darkness = measureDarkness(current.context, current.size);
  }
}

/* =========================================================
   Scale inheritance
   ========================================================= */

/*
 * A stroke accepted at level L is inserted
 * into L and every finer level.
 *
 * Therefore:
 *
 * coarse strokes ⊂ fine strokes
 */
function propagateStroke(column, sourceLevel, stroke) {
  for (let level = sourceLevel; level < column.length; level += 1) {
    const cell = column[level];

    cell.strokes.push(stroke);

    drawWrappedStroke(cell.context, stroke, cell.size);
  }
}

/* =========================================================
   Construct one tone
   ========================================================= */

function fillToneColumn(column, toneIndex, tones, random) {
  const target = tones[toneIndex];

  /*
   * Important:
   *
   * coarse -> fine
   */
  for (let level = 0; level < column.length; level += 1) {
    const cell = column[level];

    cell.darkness = measureDarkness(cell.context, cell.size);

    let iteration = 0;

    while (cell.darkness < target && iteration < 4000) {
      const stroke = chooseBestStroke(
        random,
        cell.size,
        toneIndex,
        tones.length,
        column,
        level,
      );

      if (!stroke) {
        break;
      }

      propagateStroke(column, level, stroke);

      /*
       * Only the source cell needs to be
       * measured during this loop.
       *
       * Finer cells receive the stroke but
       * will be measured when their own
       * level is processed.
       */
      cell.darkness = measureDarkness(cell.context, cell.size);

      iteration += 1;
    }
  }

  /*
   * Record final darkness for every level.
   */
  for (const cell of column) {
    cell.darkness = measureDarkness(cell.context, cell.size);
  }
}

/* =========================================================
   Generate hierarchy
   ========================================================= */

function generateHierarchy(finestSize, tones, levels, random) {
  const scaleDefinitions = createScaleDefinitions(finestSize, levels);

  const columns = [];

  let previousColumn = null;

  /*
   * Important:
   *
   * light -> dark
   */
  for (let toneIndex = 0; toneIndex < tones.length; toneIndex += 1) {
    const column = createColumn(scaleDefinitions);

    inheritPreviousTone(previousColumn, column);

    fillToneColumn(column, toneIndex, tones, random);

    columns.push(column);

    previousColumn = column;
  }

  /*
   * Internally:
   *
   * columns[tone][coarse -> fine]
   *
   * Existing renderer expects:
   *
   * hierarchy[scale][tone]
   *
   * with hierarchy[0] = 256px.
   */
  const hierarchy = [];

  for (let publicLevel = 0; publicLevel < levels; publicLevel += 1) {
    const internalLevel = levels - publicLevel - 1;

    const row = [];

    for (let toneIndex = 0; toneIndex < tones.length; toneIndex += 1) {
      const source = columns[toneIndex][internalLevel];

      row.push({
        canvas: source.canvas,

        size: source.size,

        darkness: source.darkness,

        targetDarkness: tones[toneIndex],

        strokeCount: source.strokes.length,

        strokes: source.strokes,
      });
    }

    hierarchy.push(row);
  }

  return {
    hierarchy,
    columns,
  };
}

/* =========================================================
   GPU mip texture
   ========================================================= */

function createMipTexture(hierarchy, toneIndex) {
  const finest = hierarchy[0][toneIndex];

  const texture = new THREE.DataTexture(
    canvasToRGBA(finest.canvas),
    finest.size,
    finest.size,
    THREE.RGBAFormat,
    THREE.UnsignedByteType,
  );

  texture.generateMipmaps = false;

  const mipmaps = [];

  /*
   * Explicit TAM levels:
   *
   * 128
   * 64
   * 32
   * 16
   */
  for (let level = 1; level < hierarchy.length; level += 1) {
    const cell = hierarchy[level][toneIndex];

    mipmaps.push({
      data: canvasToRGBA(cell.canvas),

      width: cell.size,

      height: cell.size,
    });
  }

  /*
   * Complete mip chain:
   *
   * 8
   * 4
   * 2
   * 1
   */
  let previous = mipmaps[mipmaps.length - 1];

  while (previous.width > 1 || previous.height > 1) {
    const next = downsampleRGBA(previous.data, previous.width, previous.height);

    mipmaps.push(next);

    previous = next;
  }

  texture.mipmaps = mipmaps;

  texture.wrapS = THREE.RepeatWrapping;

  texture.wrapT = THREE.RepeatWrapping;

  texture.minFilter = THREE.LinearMipmapLinearFilter;

  texture.magFilter = THREE.LinearFilter;

  texture.colorSpace = THREE.NoColorSpace;

  texture.needsUpdate = true;

  return texture;
}

/* =========================================================
   Public API
   ========================================================= */

export function generateTAM({
  size = DEFAULT_SIZE,
  tones = DEFAULT_TONES,
  levels = DEFAULT_LEVELS,
  seed = 1337,
} = {}) {
  const random = createRandom(seed);

  const { hierarchy, columns } = generateHierarchy(size, tones, levels, random);

  const textures = tones.map((_, toneIndex) =>
    createMipTexture(hierarchy, toneIndex),
  );

  /*
   * Finest + darkest contains every stroke
   * ultimately present in the TAM.
   */
  const finestDarkest = hierarchy[0][tones.length - 1];

  const scaleStrokeCounts = hierarchy.map((row) =>
    row.map((cell) => cell.strokeCount),
  );

  return {
    hierarchy,

    textures,

    images: hierarchy[0].map((cell) => ({
      canvas: cell.canvas,

      targetDarkness: cell.targetDarkness,

      actualDarkness: cell.darkness,
    })),

    tones: [...tones],

    size,

    levels,

    strokeCount: finestDarkest.strokeCount,

    strokes: [...finestDarkest.strokes],

    scaleStrokeCounts,

    columns,
  };
}

/* =========================================================
   Cleanup
   ========================================================= */

export function disposeTAM(tam) {
  if (!tam || !tam.textures) {
    return;
  }

  for (const texture of tam.textures) {
    texture.dispose();
  }
}
